import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { from_date: "", to_date: "", type: "vacation", reason: "" };

/**
 * A guard files their OWN leave, from their own phone.
 *
 * The API already allowed this: `POST /guards/{id}/leaves` refuses a guard
 * filing for anyone else, and lands the request as `pending` rather than
 * approved because a guard is not in HR_ROLES. `GET /leaves` scopes itself to
 * the caller's guard_id. Only the screen was missing, so the route existed and
 * no guard could reach it.
 */
export default function MobileLeave() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [types, setTypes] = useState(["vacation", "sick", "emergency"]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [clash, setClash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sil, setSil] = useState(null);

  const load = () =>
    api.get("/leaves").then((r) => setLeaves(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/config").then((r) => r.data?.leave_types && setTypes(r.data.leave_types)).catch(() => {});
    // The balance belongs HERE, next to the button that spends it. A guard
    // filing leave without knowing what they have left is the one person in
    // the agency who cannot see the number the decision turns on.
    api.get("/my/benefits")
      .then((r) => setSil(r.data.service_incentive_leave))
      .catch(() => setSil(false));
  }, []);

  // A guard who books leave over a post they are already assigned to leaves that
  // post UNFILLED, and nobody finds out until the shift starts. /shifts is
  // self-scoped for a guard, so this costs one call and is worth it.
  const { from_date, to_date } = form;
  useEffect(() => {
    if (!from_date || !to_date || to_date < from_date) { setClash(null); return; }
    let cancelled = false;
    api.get(`/shifts?from_date=${from_date}&to_date=${to_date}`)
      .then((r) => {
        if (cancelled) return;
        const live = (r.data || []).filter((s) => !["cancelled", "completed"].includes(s.status));
        setClash(live.length ? live : null);
      })
      .catch(() => { if (!cancelled) setClash(null); });
    return () => { cancelled = true; };
  }, [from_date, to_date]);

  const submit = async () => {
    if (!form.from_date || !form.to_date) { toast.error("Pick both dates"); return; }
    if (form.to_date < form.from_date) { toast.error("The end date is before the start date"); return; }
    if (!user?.guard_id) { toast.error("This account is not linked to a guard record"); return; }
    setBusy(true);
    try {
      await api.post(`/guards/${user.guard_id}/leaves`, form);
      // Deliberately not "Leave approved" -- it is pending until HR decides, and
      // a guard who reads this as approval does not turn up for a shift.
      toast.success("Leave filed — pending approval");
      setForm(EMPTY);
      setClash(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const days = (l) => {
    const a = new Date(l.from_date), b = new Date(l.to_date);
    const n = Math.round((b - a) / 86400000) + 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  return (
    <div data-testid="mobile-leave">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground">My leave</span>
        <Button
          size="sm"
          variant={open ? "outline" : "default"}
          className="rounded-none h-8 text-[11px] font-bold uppercase tracking-wider"
          data-testid="leave-file-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <CalendarPlus size={14} className="mr-1.5" />
          {open ? "Cancel" : "File leave"}
        </Button>
      </div>

      {sil && (
        <div className="border border-border bg-card px-3 py-2 mb-2 flex items-baseline justify-between gap-3"
             data-testid="leave-balance">
          <div className="text-[11px] leading-snug text-muted-foreground">
            {sil.entitled_days ? (
              <>
                <span className="text-foreground font-bold">
                  {sil.balance_days} of {sil.entitled_days} paid day(s) left
                </span>{" "}
                this year. Days you do not take are paid out in cash when you leave, so they
                are not lost.
              </>
            ) : (
              <>
                Paid leave vests after {sil.vests_after_days} days of service. You are at{" "}
                <span className="text-foreground font-bold">{sil.tenure_days}</span> — you can
                still file, it is just not paid from this entitlement yet.
              </>
            )}
          </div>
          {sil.entitled_days > 0 && (
            <span className="font-heading font-black text-lg tabular-nums shrink-0">
              {sil.balance_days}
            </span>
          )}
        </div>
      )}

      {open && (
        <div className="border border-border bg-card p-3 mb-3 space-y-3" data-testid="leave-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">From</Label>
              <Input
                data-testid="leave-from" type="date" className="rounded-none mt-1 h-9"
                value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">To</Label>
              <Input
                data-testid="leave-to" type="date" className="rounded-none mt-1 h-9"
                value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase font-bold">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="leave-type" className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase font-bold">Reason</Label>
            <Input
              data-testid="leave-reason" className="rounded-none mt-1 h-9"
              placeholder="Optional, but it helps your supervisor decide"
              value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>

          {clash && (
            <div
              className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-2.5 flex gap-2"
              data-testid="leave-shift-clash"
            >
              <Warning size={16} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
              <div className="text-[11px] leading-snug">
                <span className="font-bold">
                  You are rostered for {clash.length} shift{clash.length > 1 ? "s" : ""} in these dates.
                </span>{" "}
                {clash.slice(0, 3).map((s) => `${s.date} ${s.site_name || ""}`.trim()).join(" · ")}
                {clash.length > 3 ? ` · +${clash.length - 3} more` : ""}.
                <div className="text-muted-foreground mt-1">
                  You can still file. Your supervisor has to backfill the post, so tell them as early as you can.
                </div>
              </div>
            </div>
          )}

          <Button
            className="w-full rounded-none h-11 font-heading font-bold"
            data-testid="leave-submit" disabled={busy} onClick={submit}
          >
            {busy ? "FILING…" : "SUBMIT FOR APPROVAL"}
          </Button>
          <div className="text-[10px] text-muted-foreground text-center">
            Filed leave is <span className="font-bold">pending</span> until HR approves it. Do not skip a shift until it shows APPROVED.
          </div>
        </div>
      )}

      <div className="space-y-2 mb-6" data-testid="leave-list">
        {leaves.length === 0 && (
          <div className="border border-border border-dashed p-4 text-center text-xs text-muted-foreground">
            No leave filed yet.
          </div>
        )}
        {leaves.map((l) => (
          <div key={l.id} className="border border-border bg-card p-3" data-testid={`leave-row-${l.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-mono text-xs">{l.from_date} → {l.to_date}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {l.type} · {days(l)} day{days(l) > 1 ? "s" : ""}
                </div>
              </div>
              <StatusBadge value={l.status} />
            </div>
            {l.reason && <div className="text-xs mt-2 text-muted-foreground">{l.reason}</div>}
            {l.decided_by && (
              <div className="font-mono text-[10px] text-muted-foreground mt-1">
                {l.status} by {l.decided_by}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
